import { useQuery } from '@tanstack/react-query';
import { ticketsApi } from '../../api/tickets';
import type { TicketActivity } from '../../types';

interface ActivityLogProps {
  ticketId: string;
}

const ACTIVITY_LABELS: Record<string, string> = {
  created:        'Ticket created',
  status_changed: 'Status changed',
  assigned:       'Assignment changed',
  unassigned:     'Unassigned',
  team_assigned:  'Team assigned',
  team_unassigned:'Team unassigned',
  replied:        'Replied',
  alert_repeat:   'Repeat alert merged in',
  note_added:     'Internal note added',
  internal_note:  'Internal note added',
  notify_muted:   'Muted customer notifications',
  notify_enabled: 'Enabled customer notifications',
  creator_notify_enabled:  'Enabled creator notifications',
  creator_notify_disabled: 'Disabled creator notifications',
  content_emails_enabled:  'Enabled update text in emails',
  content_emails_disabled: 'Disabled update text in emails (link-only)',
  priority_changed: 'Priority changed',
  time_logged:    'Time logged',
  resolved:       'Resolved',
  closed:         'Closed',
  escalated:      'Escalated',
  shared:         'Shared via email',
  linked:         'Linked ticket',
  unlinked:       'Unlinked ticket',
  task_added:     'Added a task',
  tasks_added:    'Added tasks from template',
  task_assigned:  'Assigned a task',
  task_unassigned:'Unassigned a task',
  task_updated:   'Edited a task',
  task_note:      'Task note added',
  task_completed: 'Completed a task',
  task_reopened:  'Reopened a task',
  task_linked:    'Linked a task to a ticket',
  task_unlinked:  'Unlinked a task from a ticket',
  task_deleted:   'Deleted a task',
  collaborator_added:   'Added collaborator',
  collaborator_removed: 'Removed collaborator',
};

function formatMeta(type: string, metadata: any): string | null {
  if (!metadata || typeof metadata !== 'object' || Object.keys(metadata).length === 0) return null;
  switch (type) {
    case 'status_changed': {
      if (!metadata.from || !metadata.to) return null;
      const transition = `${String(metadata.from).replace(/_/g, ' ')} → ${String(metadata.to).replace(/_/g, ' ')}`;
      return metadata.note ? `${transition} — ${metadata.note}` : transition;
    }
    case 'priority_changed':
      return metadata.from && metadata.to ? `${metadata.from} → ${metadata.to}` : null;
    case 'assigned':
      return metadata.assigneeName ?? metadata.teamName ?? null;
    case 'time_logged':
      return metadata.minutes != null ? `${metadata.minutes} min` : null;
    case 'shared':
      return Array.isArray(metadata.emails) ? metadata.emails.join(', ') : null;
    case 'linked':
    case 'unlinked': {
      const nums = Array.isArray(metadata.tickets) ? metadata.tickets.join(', ') : null;
      return nums ? (metadata.auto ? `${nums} (automatic)` : nums) : null;
    }
    case 'replied':
    case 'internal_note':
      return metadata.crossPostedFrom ? `cross-posted from ${metadata.crossPostedFrom}` : null;
    case 'tasks_added':
      return metadata.template ? `${metadata.template} (${metadata.count ?? '?'})` : null;
    case 'task_added':
      return metadata.title ? (metadata.assigneeName ? `${metadata.title} → ${metadata.assigneeName}` : metadata.title) : null;
    case 'task_assigned':
      return metadata.title ? `${metadata.title} → ${metadata.assigneeName ?? metadata.to ?? '?'}` : null;
    case 'task_completed':
    case 'task_reopened':
      return metadata.title ? (metadata.linkedTicket ? `${metadata.title} (via ${metadata.linkedTicket})` : metadata.title) : null;
    case 'task_linked':
    case 'task_unlinked':
      return metadata.title ? `${metadata.title} ↔ ${metadata.ticket ?? '?'}` : null;
    case 'task_unassigned':
    case 'task_updated':
    case 'task_note':
    case 'task_deleted':
      return metadata.title ?? null;
    case 'collaborator_added':
    case 'collaborator_removed':
      return Array.isArray(metadata.users) ? metadata.users.join(', ') : null;
    default:
      return null;
  }
}

function fmtDateTime(d?: string | null): string {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ' · '
    + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function ActivityLog({ ticketId }: ActivityLogProps) {
  const { data: activities = [], isLoading } = useQuery({
    queryKey: ['ticket-activity', ticketId],
    queryFn: () => ticketsApi.getActivity(ticketId),
    enabled: !!ticketId,
  });

  if (isLoading) return <div className="text-xs text-muted py-2">Loading…</div>;
  if ((activities as any[]).length === 0) return <p className="text-xs text-muted py-2">No activity yet.</p>;

  const actorName = (a: any): string => a.actor?.name ?? 'System';

  return (
    <div className="space-y-0">
      {(activities as (TicketActivity & { actor?: { name?: string } })[]).map((a, idx) => {
        const meta = formatMeta(a.type, a.metadata);
        return (
          <div key={(a as any).id ?? idx} className="flex gap-2.5 py-2 relative">
            {/* Timeline connector */}
            <div className="flex flex-col items-center shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1 shrink-0" />
              {idx < (activities as any[]).length - 1 && (
                <div className="w-px flex-1 bg-line mt-1" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pb-2">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-xs font-medium text-fg">
                  {actorName(a)}
                </span>
                <span className="text-xs text-muted">
                  {ACTIVITY_LABELS[a.type] ?? a.type}
                </span>
                {meta && (
                  <span className="text-xs text-primary font-medium truncate">
                    {meta}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted mt-0.5">{fmtDateTime(a.createdAt)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
